import React, { useEffect, useState } from 'react';
import { FC } from 'react';
import ConferenceProgramLecturer from '@components/ConferenceProgram/ConferenceProgramLecturer';
import ConferenceProgramTime from '@components/ConferenceProgram/ConferenceProgramTime';
import useTranslations from '@hooks/useTranslation';
import { getApiURL } from '../../constants';
import { COMPONENT_TYPE, IColors, IConferenceItem } from '../../interfaces/Conference';
import {
    Block,
    BlockName,
    BlockNumber,
    BlockPanel,
    BlockPanelLabel,
    Content,
    ContentAbstract,
    Empty,
    LegislativeLabel,
    Moderator,
    ModeratorDesc,
    ModeratorImage,
    ModeratorLabel,
    ModeratorName,
    Row,
    RowContent,
    RowSection,
    RowSectionAbstract,
    RowSectionAbstractLink,
    RowSectionLecturer,
    RowSectionTitle,
    RowSectionTitleLecture,
    RowTime,
    Tab,
    Tabs,
    Time,
    Title,
    Description,
    TitleSymbol,
    Wrapper,
} from './ConferenceProgram.elements';

interface ConferenceProps {
    colors?: IColors;
    conf?: IConferenceItem;
}

const ConferenceProgramTemplate: FC<ConferenceProps> = ({ colors, conf }) => {
    const { t } = useTranslations();

    const [activeSection, setActiveSection] = useState('programTab0');
    const [showAbstract, setShowAbstract] = useState('');
    const [isAbstract, setIsAbstract] = useState(false);
    const onlyModerators = conf?.lectors.filter((item) => item?.type === 0);

    const handleAbstract = (index: number, indexPart: number) => {
        if (showAbstract !== `programAbstract${index}${indexPart}`) {
            setIsAbstract(true);
            setShowAbstract(`programAbstract${index}${indexPart}`);
        } else {
            setIsAbstract(false);
            setShowAbstract('');
        }
    };

    return (
        <Wrapper id={`confSection${COMPONENT_TYPE.PROGRAM}`} colors={colors}>
            <Title colors={colors}>
                {t('conference.component.program.title')}
                <TitleSymbol colors={colors} />
            </Title>
            <Description>{conf?.conference?.redactorTexts?.programDescription}</Description>

            {conf?.conference?.schedule.length > 0 && (
                <Tabs>
                    {conf?.conference?.schedule.map((item, index) => (
                        <Tab
                            key={`scheduleTab${index}`}
                            colors={colors}
                            className={activeSection === `programTab${index}` ? 'active' : ''}
                            onClick={() => setActiveSection(`programTab${index}`)}
                        >
                            {item.title}
                        </Tab>
                    ))}
                </Tabs>
            )}

            {conf?.conference?.schedule.map((item, index) => (
                <Empty key={`scheduleContent${index}`}>
                    {activeSection === `programTab${index}` && (
                        <Content>
                            {onlyModerators.length > 0 && (
                                <>
                                    {onlyModerators.map((moderator, index2) => (
                                        <Moderator key={`moderator${index2}`}>
                                            {moderator.photo && (
                                                <ModeratorImage
                                                    src={`${getApiURL()}/media/people/${moderator?.photo}`}
                                                />
                                            )}
                                            {!moderator.photo && <ModeratorImage src="/public/avatar.svg" />}
                                            <ModeratorDesc>
                                                <ModeratorName>
                                                    {moderator?.titles?.before_name &&
                                                        `${moderator?.titles?.before_name} `}
                                                    {moderator.name} {moderator.surname}
                                                    {moderator?.titles?.after_name &&
                                                        `, ${moderator?.titles?.after_name}`}
                                                </ModeratorName>
                                                <ModeratorLabel>
                                                    {t('conference.general.moderator.male')}
                                                </ModeratorLabel>
                                            </ModeratorDesc>
                                        </Moderator>
                                    ))}
                                </>
                            )}

                            {item?.part && (
                                <>
                                    {item?.part.map((part, indexPart, parts) => (
                                        <Empty key={`partContent${indexPart}`}>
                                            {part.block_name && (
                                                <Block colors={colors}>
                                                    <BlockNumber colors={colors}>{part.block_name}</BlockNumber>
                                                    <BlockName> {part.block_subheading}</BlockName>
                                                </Block>
                                            )}

                                            {!part.block_name && (
                                                <Row
                                                    withoutBorder={
                                                        !!(
                                                            parts[indexPart + 1]?.block_name ||
                                                            item?.part.length === indexPart + 1
                                                        )
                                                    }
                                                >
                                                    {part.from && (
                                                        <>
                                                            <ConferenceProgramTime
                                                                from={part.from}
                                                                to={part.to}
                                                            ></ConferenceProgramTime>
                                                        </>
                                                    )}
                                                    {!part.from && <RowTime></RowTime>}

                                                    <RowContent>
                                                        <RowSection>
                                                            {part.block_name2 && (
                                                                <BlockPanel colors={colors}>
                                                                    <BlockPanelLabel colors={colors}>
                                                                        {part.block_name2}
                                                                    </BlockPanelLabel>
                                                                </BlockPanel>
                                                            )}

                                                            {part.nonLecture_name && (
                                                                <RowSectionTitle colors={colors}>
                                                                    {part.nonLecture_name}
                                                                </RowSectionTitle>
                                                            )}

                                                            {part.lecture_name && (
                                                                <RowSectionTitleLecture colors={colors}>
                                                                    {part.lecture_name}
                                                                </RowSectionTitleLecture>
                                                            )}

                                                            {part.description && (
                                                                <>
                                                                    {showAbstract !==
                                                                        `programAbstract${index}${indexPart}` && (
                                                                        <RowSectionAbstract>
                                                                            {t('conference.general.showAbstract')}{' '}
                                                                            <RowSectionAbstractLink
                                                                                colors={colors}
                                                                                onClick={() =>
                                                                                    handleAbstract(index, indexPart)
                                                                                }
                                                                            >
                                                                                +
                                                                            </RowSectionAbstractLink>
                                                                        </RowSectionAbstract>
                                                                    )}

                                                                    {showAbstract ===
                                                                        `programAbstract${index}${indexPart}` && (
                                                                        <RowSectionAbstract>
                                                                            {t('conference.general.hideAbstract')}{' '}
                                                                            <RowSectionAbstractLink
                                                                                colors={colors}
                                                                                onClick={() =>
                                                                                    handleAbstract(index, indexPart)
                                                                                }
                                                                            >
                                                                                -
                                                                            </RowSectionAbstractLink>
                                                                        </RowSectionAbstract>
                                                                    )}

                                                                    {isAbstract &&
                                                                        showAbstract ===
                                                                            `programAbstract${index}${indexPart}` && (
                                                                            <ContentAbstract
                                                                                dangerouslySetInnerHTML={{
                                                                                    __html: part?.description,
                                                                                }}
                                                                                colors={colors}
                                                                            ></ContentAbstract>
                                                                        )}
                                                                </>
                                                            )}

                                                            {part?.lecturers.length > 0 && (
                                                                <>
                                                                    {part?.lecturers.map((lecturer, index) => (
                                                                        <RowSectionLecturer key={`lecturer${index}`}>
                                                                            {
                                                                                <ConferenceProgramLecturer
                                                                                    personKey={lecturer._key} conf={conf}
                                                                                ></ConferenceProgramLecturer>
                                                                            }
                                                                        </RowSectionLecturer>
                                                                    ))}
                                                                </>
                                                            )}
                                                        </RowSection>
                                                    </RowContent>
                                                </Row>
                                            )}
                                        </Empty>
                                    ))}
                                </>
                            )}
                        </Content>
                    )}
                </Empty>
            ))}

            <LegislativeLabel>{t('conference.component.program.rightToChange')}</LegislativeLabel>
        </Wrapper>
    );
};

export default ConferenceProgramTemplate;
